import { CerFormViewSet } from "@form/cer-form-view-set";
import { CerFormPaneSettings, CerFormPanesSettings, CerFormPanesSettingsUtil, CerFormPaneType } from "./cer-form-panes.service";
import { CerFormComponent } from "@form/cer-form.component";
import { CerFormPaneComponent, CerFormPaneEventCommandType, CerFormPaneViewEvent } from "./cer-form-pane/cer-form-pane.component";
import { CerFormPaneView } from "./cer-form-pane/cer-form-pane-view";
import { QueryList } from "@angular/core";
import { UiActionTypeEnum } from "@ui/cer-ui-types";
import { CerFormRouteOptions } from "@form/cer-form-route-options";

export class CerFormPaneViewFactory {

  constructor(private form: CerFormComponent, private viewSets: CerFormViewSet[]) {
  }

  private panes: CerFormPanesSettings = this.form?.panes;
  private panesDynamic: boolean = this.form?.panesDynamic;
  private paneComponents: QueryList<CerFormPaneComponent> = this.form?.paneComponents;

  public viewSetShow(viewSet: CerFormViewSet = null, viewSetNewBuild: CerFormViewSet = null) {
    //CerFormSetupLoader.panesFromSetup(this.panes, this.viewSet);
    if (viewSetNewBuild != null) {
      this.viewSetPatchRouteOptions(viewSet, viewSetNewBuild.routeOptions);
    }
    this.viewSetsSync(viewSet);
  }

  public viewSetsSync(viewSetToActivate: CerFormViewSet = null) {
    if (!this.panesDynamic) {
      this.viewSets.forEach(vs => this.formNonDynamicMoveToExistingDynamicPane(vs));
    }

    const activePaneViews = this.viewSets?.flatMap(s => s.paneViews);
    const activePaneTypes: CerFormPaneType[] = [...new Set(activePaneViews.map(pv => pv.view.paneTarget))];

    if (this.panesDynamic) {
      if (this.panesBuildDynamic(activePaneTypes, activePaneViews)) {
        if (this.form) {
          this.form.panes = this.panes;
          this.form.panesService.panesChanged$.next(this.panes);
        }
        return; // Await rendering
      }
    }

    if (this.paneComponents == null || this.paneComponents.length == 0) {
      return; // Await rendering
    }

    const activePaneTypesSync: CerFormPaneType[] = [...new Set(activePaneViews.map(pv => pv.view.paneTarget))];
    activePaneTypesSync.forEach(activePaneType =>
      this.paneViewsSync(activePaneViews, activePaneType, viewSetToActivate)
    );
  }

  private viewSetPatchRouteOptions(viewSetFound: CerFormViewSet, routeOptions: CerFormRouteOptions) {
    // Ensure existing tab visible
    if (viewSetFound.paneViews.length > 0) {
      viewSetFound.paneViews[0].activate = true;
    }
    viewSetFound.paneViews.forEach(pv => {
      if (pv.visible == false && pv.view.visibleDefault == 'show') {
        pv.visible = true;
      }
    });
    // Any changed query parameters are updated
    if (routeOptions != null) {
      if (viewSetFound.routeOptions == null) {
        viewSetFound.routeOptions = routeOptions;
      }
      else {
        if (routeOptions.navigation != null) {
          viewSetFound.routeOptions.navigation = routeOptions.navigation;
        }
        if (routeOptions.callerData != null) {
          viewSetFound.routeOptions.callerData = routeOptions.callerData;
        }
      }
    }
  }

  private formNonDynamicMoveToExistingDynamicPane(viewSetToAdd: CerFormViewSet) {
    if (this.panes) {
      viewSetToAdd.paneViews.forEach(pv => {
        if (!this.form.panesDynamic) {
          const pane = this.panes[pv.view.paneTarget];
          if (pane == null ||
            (CerFormPanesSettingsUtil.paneIsHidden(pane) || pane.dynamicOverride !== true)) {
            // Pane is hidden, and cannot be dynamicly shown => move paneView to other visible pane
            pv.view.moveToPane(CerFormPanesSettingsUtil.paneDefaultGet(this.panes));
          }
        }
      });
    }
  }

  private panesBuildDynamic(activePaneTypes: CerFormPaneType[], activePaneViews: CerFormPaneView[]): boolean {
    var changed = false;
    if (this.panes == null) {
      this.panes = new CerFormPanesSettings();
      changed = true;
    }
    var paneCountEnabled: number = 0;
    activePaneTypes.forEach(activePaneType => {
      const paneViews = activePaneViews.filter(pv => pv.view.paneTarget == activePaneType && pv.view.visibleFollowPane !== true && pv.visible == true);
      const paneHasVisibleViews = paneViews.length > 0;
      const paneSettings = this.viewSets.filter(s => s.panes != null && s.panes[activePaneType] != null).flatMap(s => s.panes[activePaneType]);

      if (!this.panes[activePaneType]) {
        changed = true;
      }
      const pane = (paneSettings.length > 0 ? paneSettings[0] : this.panes[activePaneType]) ?? new CerFormPaneSettings(); // TODO: Expand logic? size from array.. max?
      this.panes[activePaneType] = pane;
      if (CerFormPanesSettingsUtil.paneEnsureVisible(pane, paneHasVisibleViews)) {
        changed = true;
      }
      if (pane.visible != 'hide') {
        paneCountEnabled++;
      }
    });
    if (paneCountEnabled == 0) {
      CerFormPanesSettingsUtil.panesAddOverflow(this.panes);
    }
    return changed;
  }


  private paneViewsSync(activePaneViews: CerFormPaneView[], activePaneType: CerFormPaneType, viewSetToActivate: CerFormViewSet) {
    const paneViews = activePaneViews.filter(pv => pv.view.paneTarget == activePaneType && pv.visible);
    if (paneViews?.length > 0) {
      const paneViewsAdd = viewSetToActivate?.paneViews.filter(pv => pv.view.paneTarget == activePaneType && pv.visible);
      const paneViewToActivate = paneViewsAdd?.length > 0 ? paneViewsAdd[0] : null;
      paneViews.forEach(pv => pv.activate = (pv == paneViewToActivate)); // Activate (set active) 1 pane view per pane (priority is from newly added setup)
      this.paneViewsAddToPane(activePaneType, paneViews);
    }
  }

  private paneViewsAddToPane(activePaneType: CerFormPaneType, paneViews: CerFormPaneView[]) {

    var paneComponent = this.paneComponents?.find(p => p.pane == activePaneType);
    if (paneComponent == null) {
      paneComponent = this.paneComponentAlternativeGet(activePaneType, paneViews);
      if (paneComponent == null) {
        return;
      }
      activePaneType = paneComponent.pane;
    }
    this.paneViewAddToComponent(paneViews, activePaneType, paneComponent);
  }

  private paneComponentAlternativeGet(activePaneType: CerFormPaneType, paneViews: CerFormPaneView[]): CerFormPaneComponent {
    const paneComponentCount: number = this.paneComponents?.length ?? 0;
    const paneComponentsReady: boolean =
      (
        (paneComponentCount > 0 && !CerFormPanesSettingsUtil.paneIsVisible(this.panes[activePaneType])) ||
        (paneComponentCount >= CerFormPanesSettingsUtil.paneVisibleCount(this.panes))
      );
    // Panes are created - select visible pane if active is not  available
    return paneComponentsReady ? this.paneComponents.last : null; // If component is missing, redirect to first pane component as a fall-back
  }

  private paneViewAddToComponent(paneViews: CerFormPaneView[], activePaneType: CerFormPaneType, paneComponent: CerFormPaneComponent) {
    if (paneViews?.filter(pv => pv.visible == true && pv.view.visibleDefault == 'show').length > 0) {
      if (this.panes != null && this.panes[activePaneType].visible != 'show') {
        this.form.panesService.visibleSetDo(paneComponent.pane, 'show');
      }
    }
    this.paneComponentSubscribe(paneComponent);
    paneViews.forEach(pv =>
      pv.view.moveToPane(activePaneType) // Ensure ref if moved to another pane
    )
    paneComponent.viewsAdd(paneViews);
    this.form.formService.formDesignChangeNext(UiActionTypeEnum.PanesShow);
  }

  // Events
  private paneSubscriptions: CerFormPaneComponent[] = [];
  private paneComponentSubscribe(pane: CerFormPaneComponent) {
    if (!pane != null && this.paneSubscriptions.find(paneSub => paneSub == pane) == null) {
      this.paneSubscriptions.push(pane);
      this.form.subscriptionManager.add(pane.event$.subscribe(e => this.onChildPaneEvent(e)));
    }
  }

  private eventTrigger(command: CerFormPaneEventCommandType, paneView: CerFormPaneView, disableExternal: boolean = false) {
    const paneTarget = paneView.view.paneTarget;
    const e: CerFormPaneViewEvent = { command: command, pane: paneTarget, paneView: paneView };
    const paneComponent = this.paneComponents?.find(p => p.pane == e.pane);
    if (paneComponent) {
      if (!disableExternal) {
        paneComponent.onPaneEvent(e);
      }
      if (paneComponent.isEmpty()) {
        this.form.panesService.visibleSet(paneTarget, 'hide');
      }
    }
  }

  private onChildPaneEvent(e: CerFormPaneViewEvent) {
    switch (e.command) {
      case 'remove':
        if (e.paneView) {
          this.paneViewRemove(e.paneView, true);
        }
        break;
    }
  }

  public paneViewSelect(paneView: CerFormPaneView) {
    var paneComponent = this.paneComponents?.find(p => p.pane == paneView.view.paneTarget);
    if (paneComponent != null) {
      paneView.activate = true;
      this.eventTrigger('select', paneView, false);
    }
  }

  private paneViewRemove(paneView: CerFormPaneView, disableExternalTrigger: boolean = false): boolean {
    if (paneView.visible !== true) {
      return false;
    }
    paneView.visible = false;
    this.eventTrigger('remove', paneView, disableExternalTrigger);
    if (paneView.id?.length > 0) {
      this.viewSets.reverse().forEach(viewSet => {
        viewSet.paneViews.forEach(pv => {
          if (pv.view.parentView?.id == paneView.id && pv.visible) {
            this.paneViewRemove(pv, false);
          }
        });
      });
    }
    return true;
  }
}